import { type ReactNode, useState } from 'react';

import { Button } from '@nesso/ui/primitives/button';
import { DialogClose, DialogFooter } from '@nesso/ui/primitives/dialog';

import { InlineMultiSelect } from '../../inline-multi-select';
import type { MultiSelectOption } from '../multi-select-option';

type Props = {
    onOpenChange: (open: boolean) => void;
    options: MultiSelectOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    maxSelected?: number;
    groupOrder?: string[];
    searchPlaceholder?: string;
    notFoundMessage: string;
    deleteBadgeIcon: ReactNode;
    disabled?: boolean;
    loading: boolean;
    badgeClassName?: string;
};

// Rendered inside `DialogContent`, which unmounts while closed, so the draft starts from the
// committed selection on every open — including the opens the parent drives by setting `open`
// itself, which never reach Radix's change handler.
export function MultiSelectDialogBody({
    onOpenChange,
    options,
    selected,
    onChange,
    maxSelected,
    groupOrder,
    searchPlaceholder,
    notFoundMessage,
    deleteBadgeIcon,
    disabled,
    loading,
    badgeClassName,
}: Props) {
    // Edits are staged in `draft` and only committed on Update; Cancel/close discards.
    const [draft, setDraft] = useState<string[]>(selected);

    const handleUpdate = () => {
        onChange(draft);
        onOpenChange(false);
    };

    return (
        <>
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <InlineMultiSelect
                    options={options}
                    selected={draft}
                    onChange={setDraft}
                    maxSelected={maxSelected}
                    searchPlaceholder={searchPlaceholder}
                    notFoundMessage={notFoundMessage}
                    deleteBadgeIcon={deleteBadgeIcon}
                    disabled={disabled}
                    loading={loading}
                    groupOrder={groupOrder}
                    badgeClassName={badgeClassName}
                />
            </div>
            <DialogFooter className="flex-row gap-2 p-6">
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        Cancel
                    </Button>
                </DialogClose>
                <Button className="flex-1" onClick={handleUpdate}>
                    Update
                </Button>
            </DialogFooter>
        </>
    );
}
